import React, { JSX } from 'react';
import {
  Skeleton,
  SkeletonBlock,
  SkeletonContent,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';

/**
 * Generic page-level skeleton that mirrors the standard page layout:
 * breadcrumb/title → navigation → content.
 *
 * Use as a Suspense fallback or while top-level page data is loading.
 *
 * @example
 * <Suspense fallback={<PageSkeleton />}>
 *   <MyPage />
 * </Suspense>
 */
export const PageSkeleton = (): JSX.Element => (
  <div
    className="sui-flex sui-flex-wrap sui-flex-auto sui-w-full sui-mt-0 sui-mr-auto sui-mb-0 sui-ml-auto sui-max-w-screen-2xl"
    data-testid="page-skeleton"
    aria-busy="true"
    aria-label="Loading page content"
  >
    <div className="sui-w-full">
      {/* Breadcrumb + page title — mirrors PageLayout header */}
      <div className="sui-flex sui-mb-8 sui-gap-2 sui-pt-[10px] sui-flex-col sui-px-4 sm:sui-px-8">
        <div className="sui-w-48">
          <SkeletonLine variant="single" fullWidth />
        </div>
        <div className="sui-w-72">
          <SkeletonLine variant="heading" fullWidth />
        </div>
      </div>

      {/* 12-col grid: left nav (3 cols) + content area (9 cols) — mirrors PageLayout */}
      <div className="sui-grid sui-grid-cols-12 md:sui-gap-5 lg:sui-gap-5">
        {/* Left nav — hidden on mobile, matches CustomerAccountNavigation column */}
        <div className="sui-hidden lg:sui-block lg:sui-col-span-3">
          <Skeleton orientation="vertical" grow>
            <SkeletonContent orientation="vertical">
              <SkeletonLine variant="heading" fullWidth />
              <SkeletonLine numberOfLines={6} fullWidth />
            </SkeletonContent>
          </Skeleton>
        </div>
        {/* Content area — full width on mobile, 9 cols on desktop */}
        <div className="sui-col-span-12 lg:sui-col-span-9 sui-px-4 sm:sui-px-8 lg:sui-px-0">
          <Skeleton
            grow
            orientation="vertical"
            SkeletonContentProps={{ initialSize: 'full' }}
          >
            <SkeletonContent orientation="vertical" initialSize="full">
              <SkeletonLine variant="heading" fullWidth />
              {[...Array(4)].map((_e, ii) => (
                <SkeletonBlock key={ii} height={24} />
              ))}
            </SkeletonContent>
          </Skeleton>
        </div>
      </div>
    </div>
  </div>
);
